import { Controller } from "@hotwired/stimulus";
import * as d3 from "d3";
class default_1 extends Controller {
    connect() {
        this.renderChart();
        window.addEventListener("resize", () => this.renderChart());
    }
    renderChart() {
        const el = this.element;
        if (!this.dataValue || !this.startValue)
            return;
        const start = new Date(this.startValue);
        const keys = ["tractor", "trailer", "tractortrailer"];
        // Build values with dates
        const values = this.dataValue.map((d, i) => {
            const date = new Date(start);
            if (this.dataValue.length === 12) {
                date.setMonth(date.getMonth() + i);
                date.setDate(1);
            }
            else {
                date.setDate(date.getDate() + i);
            }
            return {
                date,
                tractor: d.tractor || 0,
                trailer: d.trailer || 0,
                tractortrailer: d.tractortrailer || 0,
            };
        });
        const stack = d3.stack().keys(keys);
        const series = stack(values);
        const margin = { top: 20, right: 20, bottom: 40, left: 50 };
        const width = el.clientWidth - margin.left - margin.right;
        const height = 300 - margin.top - margin.bottom;
        el.innerHTML = "";
        const svg = d3
            .select(el)
            .append("svg")
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom)
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);
        const x = d3
            .scaleBand()
            .domain(values.map((d) => d.date))
            .range([0, width])
            .padding(0.2);
        const y = d3
            .scaleLinear()
            .domain([
            0,
            d3.max(values, (d) => keys.reduce((sum, k) => sum + d[k], 0)) || 1,
        ])
            .nice()
            .range([height, 0]);
        // Gridlines
        svg
            .append("g")
            .attr("class", "grid")
            .call(d3
            .axisLeft(y)
            .tickSize(-width)
            .tickFormat(() => ""))
            .call((g) => g.select(".domain").remove())
            .call((g) => g
            .selectAll(".tick line")
            .attr("stroke", "#e5e5e5")
            .attr("stroke-dasharray", "3,3"));
        // X axis
        svg
            .append("g")
            .attr("transform", `translate(0,${height})`)
            .call(d3
            .axisBottom(x)
            .tickValues(this.dataValue.length === 12
            ? values.map((d) => d.date) // show all months
            : values.filter((_, i) => i % 5 === 0).map((d) => d.date))
            .tickFormat((d) => this.dataValue.length === 12
            ? d3.timeFormat("%b")(d) // Jan, Feb, ...
            : d3.timeFormat("%-d")(d) // 1, 2, ...
        ))
            .call((g) => g.select(".domain").attr("stroke", "#333"))
            .call((g) => g.selectAll(".tick line").attr("stroke", "#333"))
            .call((g) => g.selectAll(".tick text").attr("fill", "#666").attr("font-size", 12));
        // Y axis
        svg
            .append("g")
            .call(d3.axisLeft(y).ticks(5))
            .call((g) => g.select(".domain").attr("stroke", "#333"))
            .call((g) => g.selectAll(".tick line").attr("stroke", "#333"))
            .call((g) => g.selectAll(".tick text").attr("fill", "#666").attr("font-size", 12));
        // Axis labels
        svg
            .append("text")
            .attr("x", width / 2)
            .attr("y", height + margin.bottom - 5)
            .attr("text-anchor", "middle")
            .attr("fill", "#333")
            .attr("font-size", 12)
            .text(this.dataValue.length === 12 ? "Mois" : "Jours");
        svg
            .append("text")
            .attr("transform", "rotate(-90)")
            .attr("x", -height / 2)
            .attr("y", -margin.left + 15)
            .attr("text-anchor", "middle")
            .attr("fill", "#333")
            .attr("font-size", 12)
            .text("Nombre de vérifs");
        // Tooltip
        const tooltip = d3
            .select(el)
            .append("div")
            .attr("class", "chart-tooltip")
            .style("position", "absolute")
            .style("background", "#fff")
            .style("border", "1px solid #ddd")
            .style("border-radius", "6px")
            .style("padding", "8px 12px")
            .style("font-size", "13px")
            .style("pointer-events", "none")
            .style("opacity", 0);
        // Colors
        const color = d3
            .scaleOrdinal()
            .domain(keys)
            .range(["#004988", "#ffb82d", "#b5b5b5"]);
        // Stacked bars
        svg
            .selectAll("g.layer")
            .data(series)
            .join("g")
            .attr("class", "layer")
            .attr("fill", (d) => color(d.key))
            .selectAll("rect")
            .data((d) => d)
            .join("rect")
            .attr("x", (d) => x(d.data.date) || 0)
            .attr("y", (d) => y(d[1]))
            .attr("height", (d) => y(d[0]) - y(d[1]))
            .attr("width", x.bandwidth())
            .on("mouseover", (event, d) => {
            const total = keys.reduce((sum, k) => sum + d.data[k], 0);
            tooltip.style("opacity", 1).html(`
            <strong>${d3.timeFormat("%b %-d")(d.data.date)}</strong><br/>
            Tracteur : ${d.data.tractor}<br/>
            Remorque : ${d.data.trailer}<br/>
            Ensemble : ${d.data.tractortrailer}<br/>
            <strong>Total : ${total}</strong>
          `);
        })
            .on("mousemove", (event) => {
            const rect = el.getBoundingClientRect();
            tooltip
                .style("left", `${event.clientX - rect.left + 12}px`)
                .style("top", `${event.clientY - rect.top - 28}px`);
        })
            .on("mouseout", () => {
            tooltip.style("opacity", 0);
        });
        // Legend
        const legend = svg
            .append("g")
            .attr("class", "legend")
            .attr("transform", `translate(${width - 90}, ${margin.top - 20})` // adjust position
        );
        keys.forEach((key, i) => {
            const g = legend.append("g").attr("transform", `translate(0, ${i * 20})`);
            // Colored box
            g.append("rect")
                .attr("width", 15)
                .attr("height", 15)
                .attr("fill", color(key));
            // Label
            g.append("text")
                .attr("x", 21)
                .attr("y", 12) // vertical alignment with rect
                .attr("fill", "#333")
                .attr("font-size", 12)
                .text(key === "tractor"
                ? "Tracteur"
                : key === "trailer"
                    ? "Remorque"
                    : "Ensemble");
        });
    }
}
default_1.values = {
    data: Array,
    start: String,
};
export default default_1;
